import type { MovementTruthCommand } from '../movement/client-truth-commit';
import type { GmBoundaryVerdict } from '../statuses/gm-boundary';
import type { AppliedFrame } from './applied-frame';
import { gmStatusAppliedFrame } from './gm-applied-frame';
import { record, upper, type Row } from '../ability/context';
import { readInventoryField, readInventoryRows } from '../compile/inventory-raw-readers';
import { readItemField } from '../compile/item-field-reader';

/** Native effects and companions share the move's single delivery, using the saved revisions. */
export const movementNeedsSavedDelivery = (command: MovementTruthCommand): boolean =>
  !!(command.teleport || command.squeeze || command.burrow || command.rest
    || command.compound || command.overrun || command.stealth || command.climb || command.trap);

/** Identities must be frozen in the browser decision, before the SQL receipt is fingerprinted. */
export function pinMovementEffectIds(command: MovementTruthCommand): void {
  for (const list of [command.overrun?.verdict.statusesAdded, command.climb?.verdict.statusesAdded,
    command.stealth?.effects?.statusesAdded, record(command.trap?.fire.verdict)?.statusesAdded])
    if (Array.isArray(list)) for (const value of list) if (record(value)) value.status_id ??= crypto.randomUUID().toUpperCase();
}

export function solidMovementAppliedFrame(command: MovementTruthCommand, encounter: Row, campaign: Row | null,
  definition: (id: string) => Row | null): AppliedFrame {
  const verdict: GmBoundaryVerdict = { rows: [], ec: {}, statusesAdded: [], statusesRemoved: [], statusStacks: [],
    charges: [], playerCharactersChanged: [], ...command.status_plan };
  // Own only the changed verdict's containers; definitions and held encounter data stay borrowed.
  verdict.ec = { ...verdict.ec }; verdict.statusesAdded = [...verdict.statusesAdded]; verdict.statusesRemoved = [...verdict.statusesRemoved];
  verdict.charges = [...verdict.charges]; verdict.playerCharactersChanged = [...verdict.playerCharactersChanged];
  const parts = [command.overrun?.verdict, command.stealth?.verdict, command.stealth?.effects, command.climb?.verdict,
    record(command.trap?.fire.verdict)];
  for (const value of parts) {
    const part = record(value); if (!part) continue;
    for (const [id, fields] of Object.entries(record(part.ec) ?? {}))
      verdict.ec[id] = { ...verdict.ec[id], ...record(fields) };
    for (const key of ['statusesAdded', 'statusesRemoved', 'statusStacks', 'charges'] as const)
      if (Array.isArray(part[key])) verdict[key] = [...verdict[key], ...part[key]] as never;
    if (Array.isArray(part.pc)) verdict.playerCharactersChanged.push(...part.pc);
    if (part.spawnRows) verdict.spawnRows = part.spawnRows as GmBoundaryVerdict['spawnRows'];
  }
  verdict.ec[command.actor_id] = { ...command.updates, ...verdict.ec[command.actor_id] };
  for (const member of command.compound?.members ?? []) if (member.id)
    verdict.ec[member.id] = { ...member.after, ...verdict.ec[member.id], movement_rev: member.expected_rev + 1 };
  const frame = gmStatusAppliedFrame({ operation_id: command.operation_id, encounter_id: command.encounter_id,
    actor_id: command.actor_id, rev: command.expected_rev + 1, member_revs: {} }, verdict, encounter, campaign, definition);
  if ('cache_ref' in frame) throw new Error('movement:applied-frame-unavailable');
  frame.member_before_revs = Object.fromEntries(Object.entries(frame.member_revs).map(([id, rev]) => [id,
    id === command.actor_id ? command.expected_rev : command.compound?.members.find(member => member.id === id)?.expected_rev
      ?? command.overrun?.revs[id] ?? command.stealth?.revs[id] ?? rev]));
  if (frame.inputs?.member_catalogs) {
    const needed = new Set(verdict.statusesAdded.map(status => upper(status.status_item_id)));
    frame.inputs.member_catalogs = Object.fromEntries(Object.entries(record(frame.inputs.member_catalogs) ?? {}).map(([id, catalog]) =>
      [id, Object.fromEntries(Object.entries(record(catalog) ?? {}).filter(([key]) => needed.has(upper(key))))]));
  }
  // A scalar move never serializes unrelated columns or carried inventory.
  frame.rows = frame.rows.map(touched => touched.table === 'encounter_creatures' ? { ...touched, partial: true,
    row: Object.fromEntries(['encounter_creature_id', 'encounter_id', 'movement_rev', ...Object.keys(verdict.ec[touched.row_id] ?? {})]
      .filter(key => key in touched.row).map(key => [key, touched.row[key]])) } : touched);
  for (const member of command.compound?.members ?? []) if (member.inventory_id) {
    const held = readInventoryRows(encounter, 'encounter_inventories_arr').find(row => upper(readInventoryField(row, 'inventory_id')) === member.inventory_id);
    if (!held) throw new Error('movement:inventory-companion-not-held');
    const inventory = record(held.inventory)!;
    Object.assign(inventory, member.after);
    held.position = { ...record(held.position), ...member.after };
    frame.rows = [...frame.rows, { table: 'inventory', row_id: member.inventory_id, partial: true,
      row: { inventory_id: member.inventory_id, ...member.after }, position: member.after }];
  }
  const lifecycle = record(record(command.trap?.fire.verdict)?.lifecycle);
  for (const row of Array.isArray(lifecycle?.removals) ? lifecycle.removals : []) if (record(row) && row.status_id)
    frame.deletes = [...frame.deletes, { table: 'statuses', row_id: upper(row.status_id) }];
  return frame;
}

/** Only the save receipt's revision/scalar values can supersede the already-decided delta. */
export function savedMovementFrame(frame: AppliedFrame, result: Row): AppliedFrame {
  for (const saved of Array.isArray(result.overrun_charges) ? result.overrun_charges : []) if (record(saved)) {
    const row = frame.rows.find(row => row.table === 'charges'
      && upper(readItemField(row.row, 'charge_holder')) === upper(readItemField(saved, 'charge_holder'))
      && upper(readItemField(row.row, 'item_id')) === upper(readItemField(saved, 'item_id')));
    if (row) { row.row_id = upper(readItemField(saved, 'charge_id')); Object.assign(row.row, saved); }
  }
  const values = new Map<string, Row>();
  for (const row of [...Array.isArray(result.overrun_parties) ? result.overrun_parties : [],
    ...Array.isArray(result.stealth_parties) ? result.stealth_parties : [], ...result.trap_victim ? [result.trap_victim] : []])
    if (record(row)) values.set(upper(row.id), { ...values.get(upper(row.id)), ...record(row.values) });
  for (const [id, value] of Object.entries(record(result.members) ?? {})) {
    const saved = record(value);
    if (saved) values.set(id, { ...record(saved.position), ...(saved.rev !== undefined ? { movement_rev: saved.rev } : {}) });
  }
  // Contest receipts precede endpoint-status writes; the final actor readback wins.
  values.set(frame.actor_id, { ...values.get(frame.actor_id), ...record(result.position), movement_rev: result.rev });
  for (const row of frame.rows) {
    const saved = values.get(row.row_id); if (!saved) continue;
    Object.assign(row.row, saved);
    if (row.position) for (const key of ['x_pos', 'y_pos', 'z_pos']) if (key in saved) row.position[key] = saved[key];
    if (row.table === 'encounter_creatures' && Number.isInteger(saved.movement_rev))
      (frame.member_revs as Record<string, number>)[row.row_id] = Number(saved.movement_rev);
  }
  frame.rev = Number(result.rev);
  (frame.member_revs as Record<string, number>)[frame.actor_id] = frame.rev;
  return frame;
}
